import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { Box } from '@hivebrite/design-system/Box';
import { CardWithTitle } from '@hivebrite/design-system/Card';
import { FakeLink } from '@hivebrite/design-system/Link';
import { Section } from '@hivebrite/design-system/Section';

import {
  GUEST_OPTIONS_CURRENT_USER_VALUE,
  GUEST_OPTIONS_OTHER_VALUE,
} from 'hb-react/shared/apps/Events/hooks';
import type { TicketItem } from 'hb-react/shared/apps/Events/pages/Booking/hooks/useBooking';
import { useBooking } from 'hb-react/shared/apps/Events/pages/Booking/hooks/useBooking';
import { StandardFields } from 'hb-react/shared/apps/Events/pages/Booking/pages/Registration/components/Form/components/StandardFields';
import { TicketAddons } from 'hb-react/shared/apps/Events/pages/Booking/pages/Registration/components/Form/components/TicketAddons';
import { useAttendeeSync } from 'hb-react/shared/apps/Events/pages/Booking/pages/Registration/hooks/useAttendeeSync';
import { useForm } from 'hb-react/shared/apps/Events/pages/Booking/pages/Registration/hooks/useForm';
import type { Attendee } from 'hb-react/shared/apps/Events/types';
import {
  Checkbox,
  CustomAttribute,
  Email,
  Text,
} from 'hb-react/shared/components/formik/components/inputs';
import type { User } from 'hb-react/types';

import GuestSelect from '../GuestSelect';

export const DefaultGuestsTicketForm = ({
  currentUser,
  attendee,
  ticket,
  index,
  guestsOptions,
  deleteGuest,
  updateGuest,
  createGuest,
}: {
  currentUser: User;
  attendee: Attendee;
  ticket: TicketItem;
  index: number;
  guestsOptions: any;
  deleteGuest: any;
  updateGuest: any;
  createGuest: any;
}) => {
  const { event } = useBooking();

  const { syncAttendeeField } = useAttendeeSync();

  const [expanded, setExpanded] = useState(false);

  const { setFieldValue, values, validateForm } = useForm();
  const attendees = values?.attendees;

  const { customizable_attributes: customizableAttributes = [] } = ticket;
  const standardFields = event?.standard_fields;

  useEffect(() => {
    if (attendee?.guest_id === null && expanded) {
      setExpanded(false);
    } else if (attendee?.guest_id && !expanded) {
      setExpanded(true);
    }
  }, [attendee]);

  const formFields = useMemo(
    () => [
      'email',
      'firstname',
      'lastname',
      ...Object.values(standardFields || {}).map(({ name }) => name),
      ...Object.values(customizableAttributes || {}).map(({ name }) => name),
    ],
    [standardFields, customizableAttributes],
  );

  const setFieldValueForUser = useCallback(
    (user, i = index) => {
      formFields.forEach((field) => {
        setFieldValue(`attendees[${i}][${field}]`, user?.[field] ?? '');
      });
    },
    [index, formFields, setFieldValue],
  );

  const clearValue = useCallback(
    (i, resetGuest = false) => {
      if (resetGuest) {
        setFieldValue(`attendees[${i}][guest_id]`, GUEST_OPTIONS_OTHER_VALUE);
      }

      formFields.forEach((field) =>
        setFieldValue(`attendees[${i}][${field}]`, '', false),
      );
    },
    [formFields],
  );

  const onChange = (value) => {
    setExpanded(value !== null);

    if (value?.value === GUEST_OPTIONS_OTHER_VALUE) {
      return clearValue(index);
    }

    setFieldValue(
      `attendees[${index}][user_id]`,
      value?.value === GUEST_OPTIONS_CURRENT_USER_VALUE ? currentUser.id : '',
    );

    setFieldValueForUser(value);

    validateForm();
  };

  const onUpdate = (payload) => {
    if (attendee.guest_id === GUEST_OPTIONS_CURRENT_USER_VALUE) {
      return;
    }

    if (attendee.guest_id && attendee.guest_id !== GUEST_OPTIONS_OTHER_VALUE) {
      return updateGuest({
        guest: {
          ...attendee,
          ...payload,
        },
      });
    }

    const guestId = uuid();

    setFieldValue(`attendees[${index}][guest_id]`, guestId);

    return createGuest({
      guest: { ...attendee, ...payload, guest_id: guestId },
    });
  };

  const onDelete = useCallback(
    (guest) => {
      attendees
        .map((attendee, i) => (attendee.guest_id === guest.guest_id ? i : ''))
        .filter(String)
        .forEach((ticketIndex) => clearValue(ticketIndex, true));

      deleteGuest({ guest });
    },
    [attendees],
  );

  const onPrefill = useCallback(() => {
    attendees.forEach((_, i) => {
      if (i === index) {
        return;
      }

      setFieldValue(`attendees[${i}][guest_id]`, attendee.guest_id);
      setFieldValue(`attendees[${i}][user_id]`, attendee.user_id);

      setFieldValueForUser(attendee, i);
    });
  }, [attendees]);

  return (
    <CardWithTitle
      title={
        <>
          {__('Ticket %{index} • ').i__({ index: `${index + 1}` })}
          {ticket.name}
        </>
      }
      data-testid="event-ticket-guest"
    >
      <Box flexDirection="column">
        <Section containerPaddingX={false}>
          <Box
            gridColumn={{
              _: 'auto',
              m: '1 / 3',
            }}
            flexDirection="column"
          >
            <GuestSelect
              name={`attendees[${index}][guest_id]`}
              undeletable={[
                `${currentUser.firstname} ${currentUser.lastname}`,
                __('Other'),
                __('Add an external guest'),
              ]}
              guestsOptions={guestsOptions}
              onChange={onChange}
              onDelete={onDelete}
              wrapperProps={{ mb: 0 }}
              withoutWrapperCSS
            />

            {index === 0 && (
              <Box flexDirection="column">
                {event?.show_participants &&
                  attendee?.guest_id === GUEST_OPTIONS_CURRENT_USER_VALUE && (
                    <Box mr="auto" mt={4}>
                      <Checkbox
                        name={`attendees[${index}][hidden]`}
                        label={__('Hide me from the list of participants')}
                        labelMultiline
                        wrapperProps={{ mb: 0 }}
                      />
                    </Box>
                  )}
                {event?.same_attendee_for_all_tickets_enabled &&
                  attendee?.guest_id &&
                  attendees.length > 1 && (
                    <Box mt={4}>
                      <FakeLink onClick={onPrefill}>
                        {__('Same attendee for all tickets')}
                      </FakeLink>
                    </Box>
                  )}
              </Box>
            )}
          </Box>
          {expanded && (
            <>
              <Text
                name={`attendees[${index}][firstname]`}
                label={__('First name')}
                onChange={(firstname) => onUpdate({ firstname })}
                validate={{
                  required: true,
                }}
                wrapperProps={{ mb: 0 }}
                onBlur={() => syncAttendeeField('firstname', index)}
              />
              <Text
                name={`attendees[${index}][lastname]`}
                label={__('Last name')}
                onChange={(lastname) => onUpdate({ lastname })}
                validate={{
                  required: true,
                }}
                wrapperProps={{ mb: 0 }}
                onBlur={() => syncAttendeeField('lastname', index)}
              />

              <Email
                name={`attendees[${index}][email]`}
                label={__('Email address')}
                onChange={(email) => onUpdate({ email })}
                validate={{
                  required: true,
                }}
                wrapperProps={{ mb: 0 }}
                onBlur={() => syncAttendeeField('email', index)}
              />

              {standardFields && (
                <StandardFields
                  fields={standardFields}
                  name={`attendees[${index}]`}
                  onUpdate={onUpdate}
                  onBlur={(name) => syncAttendeeField(name, index)}
                />
              )}

              {customizableAttributes &&
                customizableAttributes.length > 0 &&
                customizableAttributes.map((attribute) => (
                  <CustomAttribute
                    key={attribute.id}
                    name={`attendees[${index}][${attribute.name}]`}
                    customAttribute={attribute}
                    // @ts-expect-error: fix types
                    labelMultiline
                    onChange={(v) => {
                      if (attribute.attribute_type === 'select' && v) {
                        return onUpdate({
                          [attribute.name]: Array.isArray(v)
                            ? v.map((v) => v.value)
                            : [v.value],
                        });
                      }

                      return onUpdate({
                        [attribute.name]: v,
                      });
                    }}
                    wrapperProps={{
                      mb: 0,
                      gridColumn: {
                        _: 'auto',
                        m:
                          attribute.attribute_type === 'double_select'
                            ? '1 / 3'
                            : 'auto',
                      },
                    }}
                  />
                ))}
            </>
          )}
        </Section>
        <TicketAddons addons={attendee.addons} ticketIndex={index} />
      </Box>
    </CardWithTitle>
  );
};
